import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Search, Check, X, Ban, Eye, Coins, Settings, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreditsDialog, setShowCreditsDialog] = useState(false);
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsNote, setCreditsNote] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      const response = await axios.get(`${API_URL}/api/admin/users`, { params });
      setUsers(response.data.users || []);
    } catch { toast.error('獲取用戶列表失敗'); }
    finally { setLoading(false); }
  };

  const approveUser = async (userId) => {
    try { await axios.post(`${API_URL}/api/admin/users/${userId}/approve`); toast.success('用戶已批准'); fetchUsers(); }
    catch { toast.error('批准失敗'); }
  };

  const rejectUser = async (userId) => {
    try { await axios.post(`${API_URL}/api/admin/users/${userId}/reject`); toast.success('用戶已拒絕'); fetchUsers(); }
    catch { toast.error('拒絕失敗'); }
  };

  const toggleUserStatus = async (userId) => {
    try { await axios.post(`${API_URL}/api/admin/users/${userId}/toggle-status`); toast.success('狀態已更新'); fetchUsers(); }
    catch { toast.error('更新失敗'); }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`確定要刪除用戶 ${email}？此操作無法撤銷。`)) return;
    try { await axios.delete(`${API_URL}/api/admin/users/${userId}`); toast.success('已刪除'); fetchUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || '刪除失敗'); }
  };

  const adjustCredits = async () => {
    if (!creditsAmount || !selectedUser) return;
    try {
      await axios.post(`${API_URL}/api/admin/users/${selectedUser.id}/adjust-credits`, { amount: parseFloat(creditsAmount), note: creditsNote });
      toast.success('點數已調整');
      setShowCreditsDialog(false); setCreditsAmount(''); setCreditsNote('');
      fetchUsers();
    } catch { toast.error('調整失敗'); }
  };

  const statusConfig = {
    pending: { label: '待審批', cls: 'bg-amber-100 text-amber-700' },
    approved: { label: '已批准', cls: 'bg-green-100 text-green-700' },
    rejected: { label: '已拒絕', cls: 'bg-red-100 text-red-700' },
    disabled: { label: '已停用', cls: 'bg-neutral-200 text-neutral-600' },
  };

  const pendingUsers = users.filter(u => u.status === 'pending');

  return (
    <>
      <header className="bg-white border-b border-neutral-200 px-4 md:px-8 py-4 md:py-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg md:text-2xl font-heading font-bold text-neutral-900">用戶管理</h1>
            <p className="text-neutral-500 text-xs md:text-sm mt-0.5">管理用戶帳號和權限</p>
          </div>
          <Button onClick={() => navigate('/admin/form-config')} variant="outline" size="sm" className="rounded-xl border-primary-300 text-primary-600 text-xs md:text-sm">
            <Settings className="w-3.5 h-3.5 mr-1.5" />
            <span className="hidden md:inline">表單配置</span>
            <span className="md:hidden">配置</span>
          </Button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-7xl mx-auto space-y-4 md:space-y-6">

          {/* Pending alert */}
          {pendingUsers.length > 0 && (
            <div className="bg-amber-50 border-l-4 border-amber-500 p-3 md:p-4 rounded-xl">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-amber-900 text-sm">有 {pendingUsers.length} 位用戶待審批</p>
                  <p className="text-xs text-amber-700">請盡快處理</p>
                </div>
                <Button onClick={() => setStatusFilter('pending')} size="sm" className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs flex-shrink-0">
                  查看
                </Button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-sm p-4 border border-neutral-200">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input placeholder="搜尋（郵箱/姓名）" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchUsers()} className="pl-10 rounded-xl text-sm" />
              </div>
              <div className="flex gap-1.5 overflow-x-auto">
                {Object.entries({ all: '全部', pending: '待審批', approved: '已批准', disabled: '已停用' }).map(([k, v]) => (
                  <Button key={k} onClick={() => setStatusFilter(k)} size="sm"
                    variant={statusFilter === k ? 'default' : 'outline'}
                    className={`rounded-full text-xs flex-shrink-0 ${statusFilter === k ? 'bg-primary-600 text-white' : ''}`}>
                    {v}
                  </Button>
                ))}
                <Button onClick={fetchUsers} size="sm" className="rounded-full bg-primary-600 text-white text-xs flex-shrink-0">搜尋</Button>
              </div>
            </div>
          </div>

          {/* Desktop: Table */}
          <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-neutral-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500">用戶</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500">郵箱</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500">狀態</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500">點數</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500">註冊</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-neutral-500">操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center py-12 text-neutral-400 text-sm">載入中...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-12 text-neutral-400 text-sm">沒有找到用戶</td></tr>
                ) : users.map(u => (
                  <tr key={u.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                    <td className="px-6 py-3 font-medium text-sm text-neutral-900">{u.name}</td>
                    <td className="px-6 py-3 text-sm text-neutral-600">{u.email}</td>
                    <td className="px-6 py-3"><span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig[u.status]?.cls || ''}`}>{statusConfig[u.status]?.label || u.status}</span></td>
                    <td className="px-6 py-3"><span className="font-semibold text-sm">{u.credits}</span></td>
                    <td className="px-6 py-3 text-xs text-neutral-500">{new Date(u.created_at).toLocaleDateString('zh-TW')}</td>
                    <td className="px-6 py-3">
                      <UserActions user={u} onApprove={approveUser} onReject={rejectUser} onToggle={toggleUserStatus}
                        onDelete={deleteUser} onCredits={(usr) => { setSelectedUser(usr); setShowCreditsDialog(true); }}
                        onView={(id) => navigate(`/admin/users/${id}`)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: Card list */}
          <div className="md:hidden space-y-3">
            {loading ? (
              <div className="text-center py-12 text-neutral-400 text-sm">載入中...</div>
            ) : users.length === 0 ? (
              <div className="text-center py-12 text-neutral-400 text-sm">沒有找到用戶</div>
            ) : users.map(u => (
              <div key={u.id} className="bg-white rounded-xl border border-neutral-200 p-4" data-testid={`user-card-${u.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-neutral-900 truncate">{u.name}</div>
                    <div className="text-xs text-neutral-500 truncate">{u.email}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ml-2 ${statusConfig[u.status]?.cls || ''}`}>
                    {statusConfig[u.status]?.label || u.status}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-3 text-xs text-neutral-500">
                    <span className="flex items-center gap-1"><Coins className="w-3 h-3 text-primary-600" /><strong className="text-neutral-800">{u.credits}</strong></span>
                    <span>{new Date(u.created_at).toLocaleDateString('zh-TW')}</span>
                  </div>
                  <div className="flex gap-1.5">
                    {u.status === 'pending' && (
                      <>
                        <Button onClick={() => approveUser(u.id)} size="sm" className="h-7 w-7 p-0 rounded-lg bg-green-600 text-white"><Check className="w-3.5 h-3.5" /></Button>
                        <Button onClick={() => rejectUser(u.id)} size="sm" variant="outline" className="h-7 w-7 p-0 rounded-lg border-red-300 text-red-600"><X className="w-3.5 h-3.5" /></Button>
                      </>
                    )}
                    {u.status === 'approved' && (
                      <Button onClick={() => toggleUserStatus(u.id)} size="sm" variant="outline" className="h-7 w-7 p-0 rounded-lg"><Ban className="w-3.5 h-3.5" /></Button>
                    )}
                    {u.status === 'disabled' && (
                      <Button onClick={() => toggleUserStatus(u.id)} size="sm" className="h-7 w-7 p-0 rounded-lg bg-green-600 text-white"><Check className="w-3.5 h-3.5" /></Button>
                    )}
                    <Button onClick={() => { setSelectedUser(u); setShowCreditsDialog(true); }} size="sm" variant="outline" className="h-7 w-7 p-0 rounded-lg border-primary-300 text-primary-600"><Coins className="w-3.5 h-3.5" /></Button>
                    <Button onClick={() => navigate(`/admin/users/${u.id}`)} size="sm" variant="outline" className="h-7 w-7 p-0 rounded-lg"><Eye className="w-3.5 h-3.5" /></Button>
                    <Button onClick={() => deleteUser(u.id, u.email)} size="sm" variant="outline" className="h-7 w-7 p-0 rounded-lg border-red-300 text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Credits Dialog */}
        {showCreditsDialog && selectedUser && (
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl border border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">調整點數</h3>
              <p className="text-sm text-neutral-600 mb-1">{selectedUser.name} ({selectedUser.email})</p>
              <p className="text-sm text-neutral-600 mb-4">當前：<strong className="text-primary-600">{selectedUser.credits}</strong></p>
              <Input type="number" step="0.01" placeholder="正數增加，負數減少" value={creditsAmount} onChange={(e) => setCreditsAmount(e.target.value)} className="rounded-xl mb-3" data-testid="admin-users-adjust-credits-amount" />
              <Input placeholder="備註（可選）" value={creditsNote} onChange={(e) => setCreditsNote(e.target.value)} className="rounded-xl mb-4" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setShowCreditsDialog(false); setCreditsAmount(''); setCreditsNote(''); }} className="flex-1 rounded-xl">取消</Button>
                <Button onClick={adjustCredits} disabled={!creditsAmount} className="flex-1 rounded-xl bg-primary-600 text-white">確認</Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

const UserActions = ({ user, onApprove, onReject, onToggle, onDelete, onCredits, onView }) => (
  <TooltipProvider delayDuration={0}>
    <div className="flex justify-end gap-1.5">
      {user.status === 'pending' && (
        <>
          <Tooltip><TooltipTrigger asChild><Button onClick={() => onApprove(user.id)} size="sm" className="h-7 w-7 p-0 rounded-lg bg-green-600 text-white"><Check className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent className="bg-neutral-900 text-white text-xs">批准</TooltipContent></Tooltip>
          <Tooltip><TooltipTrigger asChild><Button onClick={() => onReject(user.id)} size="sm" variant="outline" className="h-7 w-7 p-0 rounded-lg border-red-300 text-red-600"><X className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent className="bg-neutral-900 text-white text-xs">拒絕</TooltipContent></Tooltip>
        </>
      )}
      {user.status === 'approved' && (
        <Tooltip><TooltipTrigger asChild><Button onClick={() => onToggle(user.id)} size="sm" variant="outline" className="h-7 w-7 p-0 rounded-lg"><Ban className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent className="bg-neutral-900 text-white text-xs">停用</TooltipContent></Tooltip>
      )}
      {user.status === 'disabled' && (
        <Tooltip><TooltipTrigger asChild><Button onClick={() => onToggle(user.id)} size="sm" className="h-7 w-7 p-0 rounded-lg bg-green-600 text-white"><Check className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent className="bg-neutral-900 text-white text-xs">啟用</TooltipContent></Tooltip>
      )}
      <Tooltip><TooltipTrigger asChild><Button onClick={() => onCredits(user)} size="sm" variant="outline" className="h-7 w-7 p-0 rounded-lg border-primary-300 text-primary-600" data-testid={`admin-users-adjust-credits-${user.id}`}><Coins className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent className="bg-neutral-900 text-white text-xs">調整點數</TooltipContent></Tooltip>
      <Tooltip><TooltipTrigger asChild><Button onClick={() => onView(user.id)} size="sm" variant="outline" className="h-7 w-7 p-0 rounded-lg" data-testid={`admin-users-view-user-${user.id}`}><Eye className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent className="bg-neutral-900 text-white text-xs">詳情</TooltipContent></Tooltip>
      <Tooltip><TooltipTrigger asChild><Button onClick={() => onDelete(user.id, user.email)} size="sm" variant="outline" className="h-7 w-7 p-0 rounded-lg border-red-300 text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent className="bg-neutral-900 text-white text-xs">刪除</TooltipContent></Tooltip>
    </div>
  </TooltipProvider>
);

export default AdminUsers;
